import * as React from 'react';
import * as moment from 'moment';
import { VictoryLine, VictoryCandlestick, VictoryChart, VictoryAxis, VictoryGroup, VictoryTheme } from 'victory';
import translate from '../../../locale/translate';
import { renderEmpty } from '../common/Render';

import { IReportSettings } from '../../../interfaces';

import ISettings from './ISettings';
import { IWidgetProps } from '../MeasurementWidgetContainer';
import { format } from './UnitFormatter';

interface IProps extends IWidgetProps {
    settings: ISettings;
}

export default class Widget extends React.Component<IProps, void> {
    static propTypes = {
        nodes: React.PropTypes.array.isRequired,
        measurements: React.PropTypes.object.isRequired,
        settings: React.PropTypes.object.isRequired,
        reportSettings: React.PropTypes.object.isRequired,
    };

    renderXTick(x: number) {
        return moment(x).format('LLL');
    }

    renderYTick(y: number, unit: number) {
        return format(y, unit);
    }

    renderNoMeasurements() {
        return (
            <div className="panel panel-default panel-body text-center"><h5>{translate.gettext("No measurements available")}</h5></div>
        );
    }

    renderMeasurement(node, measurements, field: string, settings) {
        const key = `${node.id}-${field}`;

        const fieldNameAvg = `${field}AVG`;
        const fieldNameMin = `${field}MIN`;
        const fieldNameMax = `${field}MAX`;
        const fieldUnit = measurements.config.unit;
        const fieldDisplayName = measurements.config.fields[field];

        const hasErrorValue = measurements.measurements.filter(m => {
            return m.errline > 0;
        }).length > 0;

        let errLineChart;

        if (hasErrorValue) {
            errLineChart = <VictoryLine
                data={measurements.measurements}
                x="name"
                y={(datum) => datum.errline || 0}
                style={{
                    data: { stroke: "orange", opacity: 0.5 }
                }}
                />;
        }

        return (
            <div className="panel panel-default" key={key}>
                <div className="panel-heading">
                    <h5 className="panel-title">{fieldDisplayName}</h5>
                </div>
                <VictoryChart
                    height={180}
                    width={960}
                    padding={{ left: 140, right: 0, top: 5, bottom: 24 }}
                    theme={VictoryTheme.material}
                    >
                    <VictoryAxis
                        tickFormat={this.renderXTick}
                        tickCount={4}
                        style={{ tickLabels: { fontSize: 9, padding: 3 } }}
                        />
                    <VictoryAxis
                        dependentAxis
                        tickFormat={(y) => this.renderYTick(y, fieldUnit)}
                        style={{ tickLabels: { fontSize: 9, padding: 3 } }}
                        />

                    <VictoryGroup>
                        <VictoryLine
                            data={measurements.measurements}
                            x="name"
                            y={(datum) => datum[fieldNameAvg] || 0}
                            style={{
                                data: { stroke: "gray" }
                            }}
                            />
                        <VictoryCandlestick
                            data={measurements.measurements}
                            x="name"
                            close={(datum) => datum[fieldNameAvg] || 0}
                            open={(datum) => datum[fieldNameAvg] || 0}
                            high={(datum) => datum[fieldNameMax] || 0}
                            low={(datum) => datum[fieldNameMin] || 0}
                            style={{
                                data: { stroke: "gray", strokeWidth: 0.5 }
                            }}
                            />
                        {errLineChart}
                    </VictoryGroup>
                </VictoryChart>
            </div>
        );
    }

    renderNode(node, measurements, settings) {
        let children;

        if (!measurements || measurements.fields.length < 2) {
            children = this.renderNoMeasurements();
        } else {
            children = measurements.fields.filter(field => {
                return field !== 'errline';
            }).map(field => {
                return this.renderMeasurement(node, measurements, field, settings);
            });
        }

        const path = node._searchIdx.parentNames.slice(2);
        path.push(node.name);

        const fullName = path.join(' / ');

        return (
            <div key={node.id}>
                <h4>{fullName}</h4>

                {children}
            </div>
        );
    }

    renderData(nodes, measurements, settings, reportSettings: IReportSettings) {
        let isTrimmed = false;

        if(reportSettings.isEditor && nodes.length > 15) {
            nodes = nodes.slice(0, 14);
            isTrimmed = true;
        }

        const renderedNodes = nodes.map(node => {
            return this.renderNode(node, measurements[node.id], settings);
        });

        if(isTrimmed) {
            renderedNodes.push(
                <div className="alert alert-warning text-center" key="preview-limit">
                    {translate.gettext("Editor preview is limited to 15 entries")}
                </div>
            )
        }

        return (
            <div>
                {renderedNodes}
            </div>
        );
    }

    render() {
        let { nodes } = this.props;
        const { measurements, settings, reportSettings } = this.props;

        if(!settings.displayWithoutMeasurements) {
            nodes = nodes.filter(node => {
                return measurements[node.id] && measurements[node.id].fields.length > 1;
            });
        }

        let renderedData;
        if (nodes && nodes.length) {
            renderedData = this.renderData(nodes, measurements, settings, reportSettings);
        } else {
            renderedData = renderEmpty();
        }

        return (
            <div>
                {renderedData}
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/widget/listMeasurements/Widget.tsx